/**
 * ReBirth Reborn - Application principale
 * Orchestration des machines et de l'UI
 */

import { AudioEngine } from './audio/AudioEngine.js';
import { TB303 } from './audio/TB303.js';
import { TR808 } from './audio/TR808.js';
import { TR909 } from './audio/TR909.js';
import { Minimoog } from './audio/Minimoog.js';
import { KorgMS20 } from './audio/KorgMS20.js';
import { Jupiter8 } from './audio/Jupiter8.js';
import { Theremin } from './audio/Theremin.js';
import { ARP2600 } from './audio/ARP2600.js';
import { Juno106 } from './audio/Juno106.js';
import { SH101 } from './audio/SH101.js';
import { MiniMod } from './audio/MiniMod.js';
import { Transport } from './sequencer/Transport.js';
import { Sequencer } from './sequencer/Sequencer.js';
import { AudioRecorder } from './audio/AudioRecorder.js';
import { Store } from './state/Store.js';
import { getPresetNames, getPreset } from './state/Presets.js';
import { MIDIExporter } from './utils/MIDIExporter.js';
import { Visualizer } from './ui/components/Visualizer.js';
import { TB303Panel } from './ui/machines/TB303Panel.js';
import { TR808Panel } from './ui/machines/TR808Panel.js';
import { TR909Panel } from './ui/machines/TR909Panel.js';
import { MinimoogPanel } from './ui/machines/MinimoogPanel.js';
import { KorgMS20Panel } from './ui/machines/KorgMS20Panel.js';
import { Jupiter8Panel } from './ui/machines/Jupiter8Panel.js';
import { ThereminPanel } from './ui/machines/ThereminPanel.js';
import { ARP2600Panel } from './ui/machines/ARP2600Panel.js';
import { Juno106Panel } from './ui/machines/Juno106Panel.js';
import { SH101Panel } from './ui/machines/SH101Panel.js';
import { MiniModPanel } from './ui/machines/MiniModPanel.js';
import { EffectsPanel } from './ui/effects/EffectsPanel.js';
import { MixerPanel } from './ui/mixer/MixerPanel.js';
import { themeManager } from './ui/themes/ThemeManager.js';
import { PianoKeyboard } from './ui/components/PianoKeyboard.js';
import { PatternSelector } from './ui/components/PatternSelector.js';
import { SongPanel } from './ui/components/SongPanel.js';
import { ScenePanel } from './ui/components/ScenePanel.js';
import { VisualKeyboard } from './ui/keyboard/VisualKeyboard.js';
import { getPerformanceProfile } from './utils/performance.js';

export class App {
    constructor() {
        this.store = null;
        this.audioEngine = null;
        this.performance = null;
        this.tb303_1 = null;
        this.tb303_2 = null;
        this.tr808 = null;
        this.tr909 = null;
        this.minimoog = null;
        this.ms20 = null;
        this.jupiter8 = null;
        this.theremin = null;
        this.arp2600 = null;
        this.juno106 = null;
        this.sh101 = null;
        this.transport = null;
        this.sequencer = null;

        // Piano keyboard popup
        this.pianoKeyboard = null;
        this.pianoTarget = null; // { element, patternName, stepIndex, instrumentName }

        // Pattern selector (A-H)
        this.patternSelector = null;

        // Song panel
        this.songPanel = null;

        // Visual keyboard for live playing
        this.visualKeyboard = null;
    }

    init() {
        // Initialiser le store avec les patterns par défaut
        this.store = new Store();

        // Profil de performance (mobile/low power)
        this.performance = getPerformanceProfile();

        // Initialiser l'audio engine
        this.audioEngine = new AudioEngine({ performance: this.performance });

        // Créer les machines : 2x TB-303 + 1x TR-808 + 1x TR-909 + 1x Minimoog + 1x MS-20 + 1x Jupiter-8
        this.tb303_1 = new TB303(this.audioEngine, 1, { performance: this.performance });
        this.tb303_2 = new TB303(this.audioEngine, 2, { performance: this.performance });
        this.tr808 = new TR808(this.audioEngine, { performance: this.performance });
        this.tr909 = new TR909(this.audioEngine, { performance: this.performance });
        this.minimoog = new Minimoog(this.audioEngine, { performance: this.performance });
        this.ms20 = new KorgMS20(this.audioEngine, { performance: this.performance });
        this.jupiter8 = new Jupiter8({ performance: this.performance });
        this.jupiter8.connectTo(this.audioEngine.getMixer().getChannelInput('jupiter8'));
        this.theremin = new Theremin({ performance: this.performance });
        this.theremin.connectTo(this.audioEngine.getMixer().getChannelInput('theremin'));
        this.arp2600 = new ARP2600({ performance: this.performance });
        this.arp2600.connectTo(this.audioEngine.getMixer().getChannelInput('arp2600'));
        this.juno106 = new Juno106({ performance: this.performance });
        this.juno106.connectTo(this.audioEngine.getMixer().getChannelInput('juno106'));
        this.sh101 = new SH101({ performance: this.performance });
        this.sh101.connectTo(this.audioEngine.getMixer().getChannelInput('sh101'));

        // MiniMod (synthé modulaire expérimental)
        this.minimod = new MiniMod(this.audioEngine, { performance: this.performance });

        // Appliquer les paramètres synth sauvegardés
        this.applySynthParams();

        // Créer le transport et le séquenceur
        this.transport = new Transport();
        this.transport.setBpm(this.store.getBpm());
        this.transport.setSwing(this.store.getSwing());
        this.sequencer = new Sequencer(this.store, this.tb303_1, this.tb303_2, this.tr808, this.tr909, this.transport, this.minimoog, this.ms20, this.jupiter8);
        this.sequencer.setARP2600(this.arp2600);
        this.sequencer.setJuno106(this.juno106);
        this.sequencer.setSH101(this.sh101);
        this.sequencer.setMiniMod(this.minimod);

        // Audio Recorder (module isolé, peut être supprimé)
        this.audioRecorder = new AudioRecorder();
        this.audioRecorder.onStateChange = (isRecording) => this.updateRecordingUI(isRecording);

        // Visualizer (isolé, activable sur demande, pas de persistance)
        this.visualizer = null;

        // Rendre l'UI
        this.render();

        // Connecter les événements
        this.setupEventListeners();

        // Subscribe to mode/scene/song changes to update transport status
        this.store.onSceneChange(() => this.updateTransportStatus());
        this.store.onSongChange(() => this.updateTransportStatus());
        this.store.onPatternChange(() => this.updateTransportStatus());

        // Initial status update
        this.updateTransportStatus();
    }

    render() {
        const appContainer = document.getElementById('app');
        const presetOptions = getPresetNames().map(name =>
            `<option value="${name}">${name}</option>`
        ).join('');

        appContainer.innerHTML = `
            <h1>ReBirth Reborn</h1>
            <p class="subtitle">Cliquez sur "START" puis sur les steps pour créer un pattern</p>

            <div class="transport" id="mainTransport">
                <button id="startBtn" title="Démarrer/Arrêter la lecture (Espace)">▶ START</button>
                <button id="recBtn" class="rec-btn" title="Mode enregistrement: cliquez les pads pour enregistrer en temps réel (R)">⏺ REC</button>
                <div class="tempo-control" title="Tempo en battements par minute (+/- pour ajuster)">
                    <span>BPM:</span>
                    <input type="range" id="tempoSlider" min="60" max="180" value="${this.transport.bpm}">
                    <span id="tempoValue">${this.transport.bpm}</span>
                </div>
                <div class="swing-control" title="Swing/Shuffle: décale les notes paires pour un groove plus groovy">
                    <span>SWING:</span>
                    <input type="range" id="swingSlider" min="0" max="100" value="${this.transport.swing * 100}">
                    <span id="swingValue">${Math.round(this.transport.swing * 100)}%</span>
                </div>
                <div class="led" id="beatLed" title="Indicateur de tempo"></div>
                <div class="led rec-led" id="recLed" title="Indicateur d'enregistrement"></div>
                <span class="shortcut-hint">Espace: Play/Stop | +/-: BPM | R: Rec</span>
            </div>

            <!-- Transport flottant (visible au scroll) -->
            <div class="floating-transport" id="floatingTransport">
                <button id="floatingStartBtn" title="Démarrer/Arrêter la lecture (Espace)">▶ START</button>
                <button id="floatingRecBtn" class="rec-btn" title="Mode enregistrement (R)">⏺ REC</button>
                <div class="tempo-control" title="Tempo (+/-)">
                    <span>BPM:</span>
                    <input type="range" id="floatingTempoSlider" min="60" max="180" value="${this.transport.bpm}">
                    <span id="floatingTempoValue">${this.transport.bpm}</span>
                </div>
                <div class="swing-control" title="Swing/Shuffle">
                    <span>SWG:</span>
                    <input type="range" id="floatingSwingSlider" min="0" max="100" value="${this.transport.swing * 100}">
                    <span id="floatingSwingValue">${Math.round(this.transport.swing * 100)}%</span>
                </div>
                <div class="led" id="floatingBeatLed" title="Tempo"></div>
                <div class="led rec-led" id="floatingRecLed" title="Rec"></div>
                <div class="transport-status" id="transportStatus">
                    <span class="status-mode clickable" id="statusMode" title="Cliquer pour changer de mode">PATTERN</span>
                    <span class="status-separator">|</span>
                    <span class="status-detail" id="statusDetail">Bank A</span>
                </div>
            </div>

            <div class="file-controls">
                <div class="preset-control" title="Charger un pattern pré-défini dans le pattern actuel">
                    <label>Presets:</label>
                    <select id="presetSelect">
                        <option value="">-- Charger un preset --</option>
                        ${presetOptions}
                    </select>
                </div>
                <div class="theme-selector" title="Changer l'apparence visuelle de l'interface">
                    <label>Theme:</label>
                    <select id="themeSelect">
                        <option value="basic">Basic</option>
                        <option value="skeuomorphic" selected>Realistic</option>
                    </select>
                </div>
                <div class="file-buttons">
                    <button id="exportBtn" class="file-btn" title="Exporter tous les patterns et paramètres en fichier JSON">💾 Exporter</button>
                    <button id="exportMidiBtn" class="file-btn midi-btn" title="Exporter le pattern actuel en fichier MIDI (1 mesure)">🎹 MIDI Pattern</button>
                    <button id="exportSongMidiBtn" class="file-btn midi-btn" title="Exporter l'arrangement Song complet en fichier MIDI">🎼 MIDI Song</button>
                    <button id="importBtn" class="file-btn" title="Importer un fichier JSON précédemment exporté">📂 Importer</button>
                    <input type="file" id="importFile" accept=".json" style="display: none;">
                    <button id="clearBtn" class="file-btn danger" title="Effacer le pattern actuel (remet aux valeurs par défaut)">🗑️ Effacer</button>
                </div>
                <span class="save-indicator" id="saveIndicator">Sauvegarde</span>
                <button id="vizToggleBtn" class="viz-btn" title="Afficher/Masquer le visualiseur audio">🎆 Visualizer</button>
            </div>

            <div id="visualizerContainer"></div>

            <div class="machine sequencer-section" id="sequencer_section">
                <div class="machine-title">
                    <button class="collapse-btn" id="sequencer_collapse" title="Réduire/Agrandir">▼</button>
                    SEQUENCER <span class="brand">Patterns, Scenes & Keyboard</span>
                </div>
                <div class="machine-content" id="sequencer_content">
                    <div id="patternSelectorContainer"></div>
                    <div id="scenePanelContainer"></div>
                    <div id="songPanelContainer"></div>
                    <div id="visualKeyboardContainer"></div>
                </div>
            </div>

            <div id="tb303_1Container"></div>
            <div id="tb303_2Container"></div>
            <div id="tr808Container"></div>
            <div id="tr909Container"></div>
            <div id="minimoogContainer"></div>
            <div id="ms20Container"></div>
            <div id="jupiter8Container"></div>
            <div id="thereminContainer"></div>
            <div id="arp2600Container"></div>
            <div id="juno106Container"></div>
            <div id="sh101Container"></div>
            <div id="minimodContainer"></div>
            <div id="mixerContainer"></div>
            <div id="effectsContainer"></div>

            <footer>
                ReBirth Reborn - Tone.js + Web Audio API
            </footer>

            <!-- Piano Keyboard Popup -->
            <div class="piano-popup" id="pianoPopup">
                <div class="piano-popup-header">
                    <div class="piano-popup-title">
                        Note: <span class="target-info" id="pianoTargetInfo">-</span>
                    </div>
                    <button class="piano-popup-close" id="pianoClose">Fermer (Esc)</button>
                </div>
                <div id="pianoContainer"></div>
            </div>
        `;

        // Rendre les panneaux des machines
        this.tb303Panel1 = new TB303Panel(this.tb303_1, this.store, this.sequencer, 1);
        this.tb303Panel1.render(document.getElementById('tb303_1Container'));

        this.tb303Panel2 = new TB303Panel(this.tb303_2, this.store, this.sequencer, 2);
        this.tb303Panel2.render(document.getElementById('tb303_2Container'));

        this.tr808Panel = new TR808Panel(this.tr808, this.store, this.sequencer);
        this.tr808Panel.render(document.getElementById('tr808Container'));

        this.tr909Panel = new TR909Panel(this.tr909, this.store, this.sequencer);
        this.tr909Panel.render(document.getElementById('tr909Container'));

        this.minimoogPanel = new MinimoogPanel(this.minimoog, this.store, this.sequencer);
        this.minimoogPanel.render(document.getElementById('minimoogContainer'));

        this.ms20Panel = new KorgMS20Panel(this.ms20, this.store, this.sequencer);
        this.ms20Panel.render(document.getElementById('ms20Container'));

        this.jupiter8Panel = new Jupiter8Panel(this.jupiter8, this.store);
        this.jupiter8Panel.render(document.getElementById('jupiter8Container'));

        this.thereminPanel = new ThereminPanel(this.theremin, this.store);
        this.thereminPanel.render(document.getElementById('thereminContainer'));

        this.arp2600Panel = new ARP2600Panel(this.arp2600, this.sequencer, this.store);
        this.arp2600Panel.render('arp2600Container');

        this.juno106Panel = new Juno106Panel(this.juno106, this.store, this.sequencer);
        this.juno106Panel.render(document.getElementById('juno106Container'));

        this.sh101Panel = new SH101Panel(this.sh101, this.store, this.sequencer);
        this.sh101Panel.render(document.getElementById('sh101Container'));

        this.minimodPanel = new MiniModPanel(this.minimod, this.store, this.sequencer);
        this.minimodPanel.render(document.getElementById('minimodContainer'));

        this.mixerPanel = new MixerPanel(this.audioEngine, this.store);
        this.mixerPanel.render(document.getElementById('mixerContainer'));
        // Restaurer l'état du mixer depuis le store
        this.mixerPanel.loadState(this.store.getMixerState());

        this.effectsPanel = new EffectsPanel(this.audioEngine, this.store);
        this.effectsPanel.render(document.getElementById('effectsContainer'));

        // Créer le clavier piano
        this.createPianoKeyboard();

        // Créer le sélecteur de patterns (A-H)
        this.patternSelector = new PatternSelector(this.store, () => {
            // Callback quand on change de pattern
            this.refreshUI();
        });
        this.patternSelector.render(document.getElementById('patternSelectorContainer'));

        // Créer le panneau Scene (arrangement avec scenes par instrument)
        this.scenePanel = new ScenePanel(this.store);
        this.scenePanel.render(document.getElementById('scenePanelContainer'));

        // Créer le panneau Song Mode
        this.songPanel = new SongPanel(this.store);
        this.songPanel.render(document.getElementById('songPanelContainer'));

        // Créer le clavier visuel pour jouer en temps réel
        this.visualKeyboard = new VisualKeyboard({
            tb303_1: this.tb303_1,
            tb303_2: this.tb303_2,
            minimoog: this.minimoog,
            ms20: this.ms20,
            jupiter8: this.jupiter8,
            arp2600: this.arp2600,
            juno106: this.juno106,
            sh101: this.sh101
        });
        this.visualKeyboard.render(document.getElementById('visualKeyboardContainer'));
    }

    /**
     * Crée le clavier piano global pour la sélection de notes
     */
    createPianoKeyboard() {
        const container = document.getElementById('pianoContainer');
        this.pianoKeyboard = new PianoKeyboard({
            startOctave: 1,
            endOctave: 3,
            onNoteSelect: (note) => this.onPianoNoteSelect(note)
        });
        this.pianoKeyboard.render(container);
    }

    /**
     * Affiche le popup piano pour un sélecteur de note
     */
    showPianoPopup(selectElement, patternName, stepIndex, instrumentName) {
        this.pianoTarget = { element: selectElement, patternName, stepIndex, instrumentName };

        // Mettre à jour le titre
        document.getElementById('pianoTargetInfo').textContent =
            `${instrumentName} - Step ${stepIndex + 1}`;

        // Sélectionner la note actuelle sur le piano
        const currentNote = this.store.patterns[patternName][stepIndex];
        this.pianoKeyboard.setSelectedNote(currentNote);

        // Afficher le popup
        document.getElementById('pianoPopup').classList.add('visible');
    }

    /**
     * Ferme le popup piano
     */
    hidePianoPopup() {
        document.getElementById('pianoPopup').classList.remove('visible');
        this.pianoTarget = null;
    }

    /**
     * Callback quand une note est sélectionnée sur le piano
     */
    onPianoNoteSelect(note) {
        if (!this.pianoTarget) return;

        const { element, patternName, stepIndex } = this.pianoTarget;

        // Mettre à jour le store
        this.store.setStep(patternName, stepIndex, note);

        // Mettre à jour l'affichage du select
        if (element && element.tagName === 'SELECT') {
            element.value = note;
        }

        // NE PAS fermer le popup - laisser l'utilisateur tester différentes notes
        // La fermeture se fait via le bouton "Fermer" ou la touche Escape
    }

    async rerenderAllPanels() {
        // Destroy existing panels
        if (this.tb303Panel1?.destroy) this.tb303Panel1.destroy();
        if (this.tb303Panel2?.destroy) this.tb303Panel2.destroy();
        if (this.tr808Panel?.destroy) this.tr808Panel.destroy();
        if (this.tr909Panel?.destroy) this.tr909Panel.destroy();
        if (this.minimoogPanel?.destroy) this.minimoogPanel.destroy();
        if (this.ms20Panel?.destroy) this.ms20Panel.destroy();
        if (this.jupiter8Panel?.destroy) this.jupiter8Panel.destroy();
        if (this.thereminPanel?.destroy) this.thereminPanel.destroy();
        if (this.arp2600Panel?.destroy) this.arp2600Panel.destroy();
        if (this.juno106Panel?.destroy) this.juno106Panel.destroy();
        if (this.sh101Panel?.destroy) this.sh101Panel.destroy();
        if (this.effectsPanel?.destroy) this.effectsPanel.destroy();

        // Clear containers
        document.getElementById('tb303_1Container').innerHTML = '';
        document.getElementById('tb303_2Container').innerHTML = '';
        document.getElementById('tr808Container').innerHTML = '';
        document.getElementById('tr909Container').innerHTML = '';
        document.getElementById('minimoogContainer').innerHTML = '';
        document.getElementById('ms20Container').innerHTML = '';
        document.getElementById('jupiter8Container').innerHTML = '';
        document.getElementById('thereminContainer').innerHTML = '';
        document.getElementById('arp2600Container').innerHTML = '';
        document.getElementById('juno106Container').innerHTML = '';
        document.getElementById('sh101Container').innerHTML = '';
        document.getElementById('minimodContainer').innerHTML = '';
        document.getElementById('effectsContainer').innerHTML = '';

        // Re-create panels with current theme
        this.tb303Panel1 = new TB303Panel(this.tb303_1, this.store, this.sequencer, 1);
        this.tb303Panel1.render(document.getElementById('tb303_1Container'));

        this.tb303Panel2 = new TB303Panel(this.tb303_2, this.store, this.sequencer, 2);
        this.tb303Panel2.render(document.getElementById('tb303_2Container'));

        this.tr808Panel = new TR808Panel(this.tr808, this.store, this.sequencer);
        this.tr808Panel.render(document.getElementById('tr808Container'));

        this.tr909Panel = new TR909Panel(this.tr909, this.store, this.sequencer);
        this.tr909Panel.render(document.getElementById('tr909Container'));

        this.minimoogPanel = new MinimoogPanel(this.minimoog, this.store, this.sequencer);
        this.minimoogPanel.render(document.getElementById('minimoogContainer'));

        this.ms20Panel = new KorgMS20Panel(this.ms20, this.store, this.sequencer);
        this.ms20Panel.render(document.getElementById('ms20Container'));

        this.jupiter8Panel = new Jupiter8Panel(this.jupiter8, this.store);
        this.jupiter8Panel.render(document.getElementById('jupiter8Container'));

        this.thereminPanel = new ThereminPanel(this.theremin, this.store);
        this.thereminPanel.render(document.getElementById('thereminContainer'));

        this.arp2600Panel = new ARP2600Panel(this.arp2600, this.sequencer, this.store);
        this.arp2600Panel.render('arp2600Container');

        this.juno106Panel = new Juno106Panel(this.juno106, this.store, this.sequencer);
        this.juno106Panel.render(document.getElementById('juno106Container'));

        this.sh101Panel = new SH101Panel(this.sh101, this.store, this.sequencer);
        this.sh101Panel.render(document.getElementById('sh101Container'));

        this.minimodPanel = new MiniModPanel(this.minimod, this.store, this.sequencer);
        this.minimodPanel.render(document.getElementById('minimodContainer'));

        this.effectsPanel = new EffectsPanel(this.audioEngine, this.store);
        this.effectsPanel.render(document.getElementById('effectsContainer'));
    }

    setupEventListeners() {
        // Sequencer section collapse
        const sequencerCollapse = document.getElementById('sequencer_collapse');
        if (sequencerCollapse) {
            // Restore collapsed state
            const isCollapsed = localStorage.getItem('sequencer_collapsed') === 'true';
            if (isCollapsed) {
                document.getElementById('sequencer_section').classList.add('collapsed');
                document.getElementById('sequencer_content').classList.add('hidden');
                sequencerCollapse.textContent = '▶';
            }

            sequencerCollapse.addEventListener('click', (e) => {
                e.stopPropagation();
                const section = document.getElementById('sequencer_section');
                const content = document.getElementById('sequencer_content');
                const isNowCollapsed = !section.classList.contains('collapsed');

                if (isNowCollapsed) {
                    section.classList.add('collapsed');
                    content.classList.add('hidden');
                    sequencerCollapse.textContent = '▶';
                } else {
                    section.classList.remove('collapsed');
                    content.classList.remove('hidden');
                    sequencerCollapse.textContent = '▼';
                }
                localStorage.setItem('sequencer_collapsed', isNowCollapsed);
            });
        }

        // Start/Stop
        document.getElementById('startBtn').addEventListener('click', async () => {
            const wasPlaying = this.transport.isPlaying;
            await this.transport.toggle();
            // Reset sequencer quand on arrête (pour recommencer au début)
            if (wasPlaying && !this.transport.isPlaying) {
                this.sequencer.reset();
            }
            this.updateTransportUI();
        });

        // Record
        document.getElementById('recBtn').addEventListener('click', () => this.toggleRecording());

        // Tempo
        document.getElementById('tempoSlider').addEventListener('input', (e) => {
            const bpm = parseInt(e.target.value);
            this.transport.setBpm(bpm);
            document.getElementById('tempoValue').textContent = bpm;
        });

        // Swing
        document.getElementById('swingSlider').addEventListener('input', (e) => {
            const swing = parseInt(e.target.value) / 100;
            this.transport.setSwing(swing);
            this.syncSwingDisplays(swing);
            this.store.setSwing(swing);
        });

        // Écouter les changements de step
        this.sequencer.onStepChange((step) => {
            this.updateStepDisplay(step);
            this.flashLed();
        });

        // Preset selector
        document.getElementById('presetSelect').addEventListener('change', (e) => {
            const presetName = e.target.value;
            if (presetName) {
                const preset = getPreset(presetName);
                if (preset) {
                    this.store.loadPreset(preset);
                    this.applySynthParams();
                    this.refreshUI();
                    this.showSaveIndicator();
                }
                e.target.value = '';
            }
        });

        // Theme selector
        document.getElementById('themeSelect').addEventListener('change', async (e) => {
            const themeName = e.target.value;
            if (themeName !== themeManager.getTheme()) {
                await themeManager.loadTheme(themeName);
                themeManager.setTheme(themeName);
                this.rerenderAllPanels();
            }
        });

        // Export button
        document.getElementById('exportBtn').addEventListener('click', () => {
            const timestamp = new Date().toISOString().slice(0, 10);
            this.store.exportToFile(`rebirth-pattern-${timestamp}.json`);
        });

        // Export MIDI Pattern button
        document.getElementById('exportMidiBtn').addEventListener('click', () => {
            const midiExporter = new MIDIExporter();
            const blob = midiExporter.export(this.store, this.transport.bpm);
            const timestamp = new Date().toISOString().slice(0, 10);
            const patternId = this.store.getCurrentPatternId();
            midiExporter.download(blob, `rebirth-pattern-${patternId}-${timestamp}.mid`);
        });

        // Export MIDI Song button
        document.getElementById('exportSongMidiBtn').addEventListener('click', () => {
            const midiExporter = new MIDIExporter();
            const blob = midiExporter.exportSong(this.store, this.transport.bpm);
            const timestamp = new Date().toISOString().slice(0, 10);
            const song = this.store.getSong();
            const totalBars = song.reduce((sum, step) => sum + step.bars, 0);
            midiExporter.download(blob, `rebirth-song-${totalBars}bars-${timestamp}.mid`);
        });

        // Import button
        document.getElementById('importBtn').addEventListener('click', () => {
            document.getElementById('importFile').click();
        });

        // Import file input
        document.getElementById('importFile').addEventListener('change', async (e) => {
            const file = e.target.files[0];
            if (file) {
                const result = await this.store.importFromFile(file);
                if (result.success) {
                    // Appliquer les paramètres synth aux instances audio
                    this.applySynthParams();
                    // Mettre à jour le BPM si présent
                    if (result.settings && result.settings.bpm) {
                        this.transport.setBpm(result.settings.bpm);
                        document.getElementById('tempoSlider').value = result.settings.bpm;
                        document.getElementById('tempoValue').textContent = result.settings.bpm;
                    }
                    this.refreshUI();
                    this.showSaveIndicator();
                } else {
                    alert('Erreur lors de l\'import du fichier');
                }
                // Reset input
                e.target.value = '';
            }
        });

        // Clear button
        document.getElementById('clearBtn').addEventListener('click', () => {
            if (confirm('Effacer tous les patterns ?')) {
                this.store.clearAll();
                this.refreshUI();
                this.showSaveIndicator();
            }
        });

        // Visualizer toggle button
        document.getElementById('vizToggleBtn').addEventListener('click', () => {
            this.toggleVisualizer();
        });

        // === FLOATING TRANSPORT ===

        // Floating Start/Stop
        document.getElementById('floatingStartBtn').addEventListener('click', async () => {
            const wasPlaying = this.transport.isPlaying;
            await this.transport.toggle();
            if (wasPlaying && !this.transport.isPlaying) {
                this.sequencer.reset();
            }
            this.updateTransportUI();
        });

        // Floating Record
        document.getElementById('floatingRecBtn').addEventListener('click', () => this.toggleRecording());

        // Floating Tempo (sync both sliders)
        document.getElementById('floatingTempoSlider').addEventListener('input', (e) => {
            const bpm = parseInt(e.target.value);
            this.transport.setBpm(bpm);
            // Sync both displays
            document.getElementById('tempoValue').textContent = bpm;
            document.getElementById('floatingTempoValue').textContent = bpm;
            document.getElementById('tempoSlider').value = bpm;
        });

        // Main tempo slider also syncs floating
        const mainTempoSlider = document.getElementById('tempoSlider');
        const originalHandler = mainTempoSlider.oninput;
        mainTempoSlider.addEventListener('input', (e) => {
            const bpm = parseInt(e.target.value);
            document.getElementById('floatingTempoValue').textContent = bpm;
            document.getElementById('floatingTempoSlider').value = bpm;
        });

        // Floating Swing (sync both sliders)
        document.getElementById('floatingSwingSlider').addEventListener('input', (e) => {
            const swing = parseInt(e.target.value) / 100;
            this.transport.setSwing(swing);
            this.syncSwingDisplays(swing);
            this.store.setSwing(swing);
        });

        // Scroll detection for floating transport
        const mainTransport = document.getElementById('mainTransport');
        const floatingTransport = document.getElementById('floatingTransport');

        window.addEventListener('scroll', () => {
            const rect = mainTransport.getBoundingClientRect();
            // Show floating transport when main is out of view
            if (rect.bottom < 0) {
                floatingTransport.classList.add('visible');
            } else {
                floatingTransport.classList.remove('visible');
            }
        });

        // Status mode click to cycle through modes (Pattern -> Scene -> Song -> Pattern)
        document.getElementById('statusMode').addEventListener('click', () => {
            const songMode = this.store.isSongMode();
            const sceneMode = this.store.isSceneMode();

            if (songMode) {
                // Song -> Pattern
                this.store.setSongMode(false);
                this.store.setSceneMode(false);
            } else if (sceneMode) {
                // Scene -> Song
                this.store.setSongMode(true);
            } else {
                // Pattern -> Scene
                this.store.setSceneMode(true);
            }
        });

        // === KEYBOARD SHORTCUTS ===

        document.addEventListener('keydown', async (e) => {
            // Ignore if typing in an input field
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT' || e.target.tagName === 'TEXTAREA') {
                return;
            }

            // Space = Play/Stop
            if (e.code === 'Space') {
                e.preventDefault();
                const wasPlaying = this.transport.isPlaying;
                await this.transport.toggle();
                if (wasPlaying && !this.transport.isPlaying) {
                    this.sequencer.reset();
                }
                this.updateTransportUI();
            }

            // R = Record
            if (e.key === 'r' || e.key === 'R') {
                e.preventDefault();
                this.toggleRecording();
            }

            // + or = : Increase BPM
            if (e.key === '+' || e.key === '=') {
                e.preventDefault();
                const newBpm = Math.min(180, this.transport.bpm + 1);
                this.transport.setBpm(newBpm);
                this.syncTempoDisplays(newBpm);
            }

            // - : Decrease BPM
            if (e.key === '-') {
                e.preventDefault();
                const newBpm = Math.max(60, this.transport.bpm - 1);
                this.transport.setBpm(newBpm);
                this.syncTempoDisplays(newBpm);
            }

            // Escape = Fermer le popup piano
            if (e.key === 'Escape') {
                this.hidePianoPopup();
            }
        });

        // === PIANO KEYBOARD POPUP ===

        // Fermer le popup piano
        document.getElementById('pianoClose').addEventListener('click', () => {
            this.hidePianoPopup();
        });

        // Intercepter les clics sur les note-select (délégation d'événements)
        document.addEventListener('click', (e) => {
            // Trouver si le clic est sur un élément .note-select
            const noteSelect = e.target.closest('.note-select');
            if (noteSelect) {
                e.preventDefault();
                e.stopPropagation();

                // Identifier l'instrument et le step
                const noteRow = noteSelect.closest('.note-row');
                if (!noteRow) return;

                // Trouver tous les selects dans cette row pour déterminer l'index
                const allSelects = noteRow.querySelectorAll('.note-select');
                const stepIndex = Array.from(allSelects).indexOf(noteSelect);
                if (stepIndex === -1) return;

                // Identifier l'instrument basé sur le parent
                let patternName = '';
                let instrumentName = '';

                if (noteRow.id.includes('tb303_1')) {
                    patternName = 'bass1Notes';
                    instrumentName = 'TB-303 #1';
                } else if (noteRow.id.includes('tb303_2')) {
                    patternName = 'bass2Notes';
                    instrumentName = 'TB-303 #2';
                } else if (noteSelect.closest('.minimoog')) {
                    patternName = 'minimoogNotes';
                    instrumentName = 'Minimoog';
                } else if (noteSelect.closest('.ms20')) {
                    patternName = 'ms20Notes';
                    instrumentName = 'MS-20';
                } else if (noteSelect.closest('.jupiter8')) {
                    patternName = 'jupiter8Notes';
                    instrumentName = 'Jupiter-8';
                } else if (noteSelect.closest('.arp2600')) {
                    patternName = 'arp2600Notes';
                    instrumentName = 'ARP 2600';
                } else if (noteSelect.closest('.juno106')) {
                    patternName = 'juno106Notes';
                    instrumentName = 'Juno-106';
                } else if (noteSelect.closest('.sh101')) {
                    patternName = 'sh101Notes';
                    instrumentName = 'SH-101';
                }

                if (patternName) {
                    this.showPianoPopup(noteSelect, patternName, stepIndex, instrumentName);
                }
            }
        }, true); // useCapture pour intercepter avant le comportement natif
    }

    /**
     * Synchronise les affichages de tempo (main + floating)
     */
    syncTempoDisplays(bpm) {
        document.getElementById('tempoValue').textContent = bpm;
        document.getElementById('floatingTempoValue').textContent = bpm;
        document.getElementById('tempoSlider').value = bpm;
        document.getElementById('floatingTempoSlider').value = bpm;
    }

    /**
     * Synchronise les affichages de swing (main + floating)
     */
    syncSwingDisplays(swing) {
        const percent = Math.round(swing * 100);
        document.getElementById('swingValue').textContent = percent + '%';
        document.getElementById('floatingSwingValue').textContent = percent + '%';
        document.getElementById('swingSlider').value = percent;
        document.getElementById('floatingSwingSlider').value = percent;
    }

    updateTransportUI() {
        const btn = document.getElementById('startBtn');
        const floatingBtn = document.getElementById('floatingStartBtn');
        const isPlaying = this.transport.isPlaying;

        [btn, floatingBtn].forEach(b => {
            if (isPlaying) {
                b.textContent = '■ STOP';
                b.classList.add('playing');
            } else {
                b.textContent = '▶ START';
                b.classList.remove('playing');
            }
        });
    }

    /**
     * Update transport status display (mode, scene, position)
     */
    updateTransportStatus() {
        const modeEl = document.getElementById('statusMode');
        const detailEl = document.getElementById('statusDetail');

        if (!modeEl || !detailEl) return;

        const songMode = this.store.isSongMode();
        const sceneMode = this.store.isSceneMode();

        // Remove all mode classes
        modeEl.classList.remove('pattern-mode', 'scene-mode', 'song-mode');

        if (songMode) {
            // Song mode
            modeEl.textContent = 'SONG';
            modeEl.classList.add('song-mode');

            const position = this.store.getSongPosition();
            const song = this.store.getSong();
            const scenes = this.store.getScenes();

            if (song.length > 0 && position.stepIndex < song.length) {
                const currentStep = song[position.stepIndex];
                const sceneName = scenes[currentStep.sceneId]?.name || 'Unknown';
                detailEl.innerHTML = `<span style="color:#fff">${sceneName}</span> <span style="color:#888">Step ${position.stepIndex + 1}/${song.length} | Bar ${position.bar + 1}/${currentStep.bars}</span>`;
            } else {
                detailEl.textContent = 'No steps';
            }
        } else if (sceneMode) {
            // Scene mode
            modeEl.textContent = 'SCENE';
            modeEl.classList.add('scene-mode');

            const currentSceneId = this.store.getCurrentSceneId();
            const scenes = this.store.getScenes();
            const sceneName = scenes[currentSceneId]?.name || 'None';
            detailEl.innerHTML = `<span style="color:#fff">${sceneName}</span>`;
        } else {
            // Pattern mode
            modeEl.textContent = 'PATTERN';
            modeEl.classList.add('pattern-mode');

            const patternId = this.store.getCurrentPatternId();
            detailEl.innerHTML = `Bank <span style="color:#00ff88;font-weight:bold">${patternId}</span>`;
        }
    }

    /**
     * Toggle recording on/off
     */
    async toggleRecording() {
        if (this.audioRecorder.getState()) {
            // Stop recording and download
            await this.audioRecorder.stopAndDownload('rebirth');
        } else {
            // Start recording
            await this.audioRecorder.start();
        }
    }

    /**
     * Toggle visualizer on/off
     */
    toggleVisualizer() {
        // Create visualizer on first use (lazy init)
        if (!this.visualizer) {
            this.visualizer = new Visualizer(this.audioEngine);
            this.visualizer.createUI('#visualizerContainer');
        }

        const isActive = this.visualizer.toggle();
        const btn = document.getElementById('vizToggleBtn');

        if (isActive) {
            btn.classList.add('active');
            btn.textContent = '🎆 Visualizer ON';
        } else {
            btn.classList.remove('active');
            btn.textContent = '🎆 Visualizer';
        }
    }

    /**
     * Update recording UI (LEDs and buttons)
     */
    updateRecordingUI(isRecording) {
        const btns = [
            document.getElementById('recBtn'),
            document.getElementById('floatingRecBtn')
        ];
        const leds = [
            document.getElementById('recLed'),
            document.getElementById('floatingRecLed')
        ];

        btns.forEach(btn => {
            if (btn) {
                btn.textContent = isRecording ? '■ STOP REC' : '⏺ REC';
                btn.classList.toggle('recording', isRecording);
            }
        });

        leds.forEach(led => {
            if (led) {
                led.classList.toggle('recording', isRecording);
            }
        });
    }

    updateStepDisplay(currentStep) {
        document.querySelectorAll('.step').forEach((step) => {
            const stepNum = parseInt(step.dataset.step);
            step.classList.toggle('current', stepNum === currentStep);
        });

        // Highlight ARP 2600 step
        if (this.arp2600Panel) {
            this.arp2600Panel.highlightStep(currentStep);
        }
    }

    flashLed() {
        const leds = [
            document.getElementById('beatLed'),
            document.getElementById('floatingBeatLed')
        ];
        leds.forEach(led => {
            led.classList.add('on');
            setTimeout(() => led.classList.remove('on'), 50);
        });
    }

    /**
     * Applique les paramètres synth du store aux instances audio
     */
    applySynthParams() {
        // TB-303 #1
        const params1 = this.store.getSynthParams('tb303_1');
        if (params1) {
            this.tb303_1.setCutoff(params1.cutoff);
            this.tb303_1.setResonance(params1.resonance);
            this.tb303_1.setEnvMod(params1.envMod);
            this.tb303_1.setDecay(params1.decay);
            this.tb303_1.setAccentLevel(params1.accentLevel);
            this.tb303_1.setWaveform(params1.waveform);
            if (params1.slideTime) this.tb303_1.setSlideTime(params1.slideTime);
            if (params1.distortion !== undefined) this.tb303_1.setDistortion(params1.distortion);
            if (params1.drive !== undefined) this.tb303_1.setDrive(params1.drive);
        }

        // TB-303 #2
        const params2 = this.store.getSynthParams('tb303_2');
        if (params2) {
            this.tb303_2.setCutoff(params2.cutoff);
            this.tb303_2.setResonance(params2.resonance);
            this.tb303_2.setEnvMod(params2.envMod);
            this.tb303_2.setDecay(params2.decay);
            this.tb303_2.setAccentLevel(params2.accentLevel);
            this.tb303_2.setWaveform(params2.waveform);
            if (params2.slideTime) this.tb303_2.setSlideTime(params2.slideTime);
            if (params2.distortion !== undefined) this.tb303_2.setDistortion(params2.distortion);
            if (params2.drive !== undefined) this.tb303_2.setDrive(params2.drive);
        }

        // TR-808
        const params808 = this.store.getSynthParams('tr808');
        if (params808) {
            this.tr808.setKickDecay(params808.kickDecay);
            this.tr808.setKickTone(params808.kickTone);
            this.tr808.setSnareSnappy(params808.snareSnappy);
            this.tr808.setHihatDecay(params808.hhDecay);
        }

        // TR-909
        const params909 = this.store.getSynthParams('tr909');
        if (params909) {
            if (params909.kickAttack !== undefined) this.tr909.setKickAttack(params909.kickAttack);
            if (params909.kickTune !== undefined) this.tr909.setKickTune(params909.kickTune);
            if (params909.snareSnappy !== undefined) this.tr909.setSnareSnappy(params909.snareSnappy);
            if (params909.snareTune !== undefined) this.tr909.setSnareTune(params909.snareTune);
            if (params909.hhDecay !== undefined) this.tr909.setHihatDecay(params909.hhDecay);
        }

        // Minimoog
        const moogParams = this.store.getSynthParams('minimoog');
        if (moogParams) {
            // OSC 1
            if (moogParams.osc1Waveform) this.minimoog.setOsc1Waveform(moogParams.osc1Waveform);
            if (moogParams.osc1Octave !== undefined) this.minimoog.setOsc1Octave(moogParams.osc1Octave);
            if (moogParams.osc1Tune !== undefined) this.minimoog.setOsc1Tune(moogParams.osc1Tune);
            if (moogParams.osc1Level !== undefined) this.minimoog.setOsc1Level(moogParams.osc1Level);
            // OSC 2
            if (moogParams.osc2Waveform) this.minimoog.setOsc2Waveform(moogParams.osc2Waveform);
            if (moogParams.osc2Octave !== undefined) this.minimoog.setOsc2Octave(moogParams.osc2Octave);
            if (moogParams.osc2Tune !== undefined) this.minimoog.setOsc2Tune(moogParams.osc2Tune);
            if (moogParams.osc2Level !== undefined) this.minimoog.setOsc2Level(moogParams.osc2Level);
            // OSC 3
            if (moogParams.osc3Waveform) this.minimoog.setOsc3Waveform(moogParams.osc3Waveform);
            if (moogParams.osc3Octave !== undefined) this.minimoog.setOsc3Octave(moogParams.osc3Octave);
            if (moogParams.osc3Tune !== undefined) this.minimoog.setOsc3Tune(moogParams.osc3Tune);
            if (moogParams.osc3Level !== undefined) this.minimoog.setOsc3Level(moogParams.osc3Level);
            // Noise
            if (moogParams.noiseLevel !== undefined) this.minimoog.setNoiseLevel(moogParams.noiseLevel);
            // Filter
            if (moogParams.cutoff !== undefined) this.minimoog.setCutoff(moogParams.cutoff);
            if (moogParams.resonance !== undefined) this.minimoog.setResonance(moogParams.resonance);
            if (moogParams.filterEnvAmount !== undefined) this.minimoog.setFilterEnvAmount(moogParams.filterEnvAmount);
            if (moogParams.filterKeyTrack !== undefined) this.minimoog.setFilterKeyTrack(moogParams.filterKeyTrack);
            // Filter Envelope
            if (moogParams.filterAttack !== undefined) this.minimoog.setFilterAttack(moogParams.filterAttack);
            if (moogParams.filterDecay !== undefined) this.minimoog.setFilterDecay(moogParams.filterDecay);
            if (moogParams.filterSustain !== undefined) this.minimoog.setFilterSustain(moogParams.filterSustain);
            // Amp Envelope
            if (moogParams.ampAttack !== undefined) this.minimoog.setAmpAttack(moogParams.ampAttack);
            if (moogParams.ampDecay !== undefined) this.minimoog.setAmpDecay(moogParams.ampDecay);
            if (moogParams.ampSustain !== undefined) this.minimoog.setAmpSustain(moogParams.ampSustain);
            if (moogParams.ampRelease !== undefined) this.minimoog.setAmpRelease(moogParams.ampRelease);
            // Master
            if (moogParams.glide !== undefined) this.minimoog.setGlide(moogParams.glide);
            if (moogParams.volume !== undefined) this.minimoog.setVolume(moogParams.volume);
        }

        // MS-20
        const ms20Params = this.store.getSynthParams('ms20');
        if (ms20Params) {
            // VCO 1
            if (ms20Params.vco1Waveform) this.ms20.setVco1Waveform(ms20Params.vco1Waveform);
            if (ms20Params.vco1Octave !== undefined) this.ms20.setVco1Octave(ms20Params.vco1Octave);
            if (ms20Params.vco1Level !== undefined) this.ms20.setVco1Level(ms20Params.vco1Level);
            // VCO 2
            if (ms20Params.vco2Waveform) this.ms20.setVco2Waveform(ms20Params.vco2Waveform);
            if (ms20Params.vco2Octave !== undefined) this.ms20.setVco2Octave(ms20Params.vco2Octave);
            if (ms20Params.vco2Detune !== undefined) this.ms20.setVco2Detune(ms20Params.vco2Detune);
            if (ms20Params.vco2Level !== undefined) this.ms20.setVco2Level(ms20Params.vco2Level);
            // Ring Mod & Noise
            if (ms20Params.ringModLevel !== undefined) this.ms20.setRingModLevel(ms20Params.ringModLevel);
            if (ms20Params.noiseLevel !== undefined) this.ms20.setNoiseLevel(ms20Params.noiseLevel);
            // HPF
            if (ms20Params.hpfCutoff !== undefined) this.ms20.setHpfCutoff(ms20Params.hpfCutoff);
            if (ms20Params.hpfResonance !== undefined) this.ms20.setHpfResonance(ms20Params.hpfResonance);
            // LPF
            if (ms20Params.lpfCutoff !== undefined) this.ms20.setLpfCutoff(ms20Params.lpfCutoff);
            if (ms20Params.lpfResonance !== undefined) this.ms20.setLpfResonance(ms20Params.lpfResonance);
            if (ms20Params.lpfEnvAmount !== undefined) this.ms20.setLpfEnvAmount(ms20Params.lpfEnvAmount);
            if (ms20Params.lpfKeyTrack !== undefined) this.ms20.setLpfKeyTrack(ms20Params.lpfKeyTrack);
            // EG1 (Filter)
            if (ms20Params.eg1Attack !== undefined) this.ms20.setEg1Attack(ms20Params.eg1Attack);
            if (ms20Params.eg1Decay !== undefined) this.ms20.setEg1Decay(ms20Params.eg1Decay);
            if (ms20Params.eg1Sustain !== undefined) this.ms20.setEg1Sustain(ms20Params.eg1Sustain);
            if (ms20Params.eg1Release !== undefined) this.ms20.setEg1Release(ms20Params.eg1Release);
            // EG2 (Amp)
            if (ms20Params.eg2Attack !== undefined) this.ms20.setEg2Attack(ms20Params.eg2Attack);
            if (ms20Params.eg2Decay !== undefined) this.ms20.setEg2Decay(ms20Params.eg2Decay);
            if (ms20Params.eg2Sustain !== undefined) this.ms20.setEg2Sustain(ms20Params.eg2Sustain);
            if (ms20Params.eg2Release !== undefined) this.ms20.setEg2Release(ms20Params.eg2Release);
            // MG (LFO)
            if (ms20Params.mgRate !== undefined) this.ms20.setMgRate(ms20Params.mgRate);
            if (ms20Params.mgPitchAmount !== undefined) this.ms20.setMgPitchAmount(ms20Params.mgPitchAmount);
            if (ms20Params.mgFilterAmount !== undefined) this.ms20.setMgFilterAmount(ms20Params.mgFilterAmount);
            // Master
            if (ms20Params.portamento !== undefined) this.ms20.setPortamento(ms20Params.portamento);
            if (ms20Params.volume !== undefined) this.ms20.setVolume(ms20Params.volume);
        }

        // Jupiter-8
        const jupiter8Params = this.store.getSynthParams('jupiter8');
        if (jupiter8Params && this.jupiter8) {
            this.jupiter8.setParams(jupiter8Params);
        }

        // Juno-106
        const juno106Params = this.store.getSynthParams('juno106');
        if (juno106Params && this.juno106) {
            this.juno106.setParams(juno106Params);
        }

        // SH-101
        const sh101Params = this.store.getSynthParams('sh101');
        if (sh101Params && this.sh101) {
            this.sh101.setParams(sh101Params);
        }

        // ARP 2600
        const arp2600Params = this.store.getSynthParams('arp2600');
        if (arp2600Params && this.arp2600) {
            this.arp2600.setParams(arp2600Params);
        }

        // Theremin
        const thereminParams = this.store.getSynthParams('theremin');
        if (thereminParams && this.theremin) {
            this.theremin.setParams(thereminParams);
        }

        // Effects (delay + reverb global params)
        const effectsParams = this.store.getSynthParams('effects');
        if (effectsParams) {
            const delay = this.audioEngine.getDelay();
            if (effectsParams.delayTime) delay.setTime(effectsParams.delayTime);
            if (effectsParams.delayFeedback !== undefined) delay.setFeedback(effectsParams.delayFeedback);
            if (effectsParams.delayMix !== undefined) delay.setMix(effectsParams.delayMix);

            const reverb = this.audioEngine.getReverb();
            if (effectsParams.reverbDecay !== undefined) reverb.setDecay(effectsParams.reverbDecay);
            if (effectsParams.reverbPreDelay !== undefined) reverb.setPreDelay(effectsParams.reverbPreDelay);
            if (effectsParams.reverbMix !== undefined) reverb.setMix(effectsParams.reverbMix);

            const compressor = this.audioEngine.getCompressor();
            if (effectsParams.compThreshold !== undefined) compressor.setThreshold(effectsParams.compThreshold);
            if (effectsParams.compRatio !== undefined) compressor.setRatio(effectsParams.compRatio);
            if (effectsParams.compAttack !== undefined) compressor.setAttack(effectsParams.compAttack);
            if (effectsParams.compRelease !== undefined) compressor.setRelease(effectsParams.compRelease);

            // Chorus
            const chorus = this.audioEngine.getChorus();
            if (effectsParams.chorusFrequency !== undefined) chorus.setFrequency(effectsParams.chorusFrequency);
            if (effectsParams.chorusDepth !== undefined) chorus.setDepth(effectsParams.chorusDepth);
            if (effectsParams.chorusMix !== undefined) chorus.setMix(effectsParams.chorusMix);

            // Phaser
            const phaser = this.audioEngine.getPhaser();
            if (effectsParams.phaserFrequency !== undefined) phaser.setFrequency(effectsParams.phaserFrequency);
            if (effectsParams.phaserQ !== undefined) phaser.setQ(effectsParams.phaserQ);
            if (effectsParams.phaserMix !== undefined) phaser.setMix(effectsParams.phaserMix);

            // Flanger
            const flanger = this.audioEngine.getFlanger();
            if (effectsParams.flangerRate !== undefined) flanger.setRate(effectsParams.flangerRate);
            if (effectsParams.flangerFeedback !== undefined) flanger.setFeedback(effectsParams.flangerFeedback);
            if (effectsParams.flangerMix !== undefined) flanger.setMix(effectsParams.flangerMix);

            // Bitcrusher
            const bitcrusher = this.audioEngine.getBitcrusher();
            if (effectsParams.crusherBits !== undefined) bitcrusher.setBits(effectsParams.crusherBits);
            if (effectsParams.crusherMix !== undefined) bitcrusher.setMix(effectsParams.crusherMix);

            // Tremolo
            const tremolo = this.audioEngine.getTremolo();
            if (effectsParams.tremoloFrequency !== undefined) tremolo.setFrequency(effectsParams.tremoloFrequency);
            if (effectsParams.tremoloDepth !== undefined) tremolo.setDepth(effectsParams.tremoloDepth);
            if (effectsParams.tremoloMix !== undefined) tremolo.setMix(effectsParams.tremoloMix);

            // Distortion
            const distortion = this.audioEngine.getDistortion();
            if (effectsParams.distortionAmount !== undefined) distortion.setAmount(effectsParams.distortionAmount);
            if (effectsParams.distortionMix !== undefined) distortion.setMix(effectsParams.distortionMix);

            // EQ
            const eq = this.audioEngine.getEQ();
            if (effectsParams.eqLow !== undefined) eq.setLow(effectsParams.eqLow);
            if (effectsParams.eqMid !== undefined) eq.setMid(effectsParams.eqMid);
            if (effectsParams.eqHigh !== undefined) eq.setHigh(effectsParams.eqHigh);
        }
    }

    /**
     * Rafraîchit toute l'interface (après import/preset)
     */
    refreshUI() {
        // Destroy existing panels
        if (this.tb303Panel1?.destroy) this.tb303Panel1.destroy();
        if (this.tb303Panel2?.destroy) this.tb303Panel2.destroy();
        if (this.tr808Panel?.destroy) this.tr808Panel.destroy();
        if (this.tr909Panel?.destroy) this.tr909Panel.destroy();
        if (this.minimoogPanel?.destroy) this.minimoogPanel.destroy();
        if (this.ms20Panel?.destroy) this.ms20Panel.destroy();
        if (this.jupiter8Panel?.destroy) this.jupiter8Panel.destroy();
        if (this.thereminPanel?.destroy) this.thereminPanel.destroy();
        if (this.arp2600Panel?.destroy) this.arp2600Panel.destroy();
        if (this.juno106Panel?.destroy) this.juno106Panel.destroy();
        if (this.sh101Panel?.destroy) this.sh101Panel.destroy();
        if (this.effectsPanel?.destroy) this.effectsPanel.destroy();

        // Re-rendre les panneaux des machines
        this.tb303Panel1 = new TB303Panel(this.tb303_1, this.store, this.sequencer, 1);
        this.tb303Panel1.render(document.getElementById('tb303_1Container'));

        this.tb303Panel2 = new TB303Panel(this.tb303_2, this.store, this.sequencer, 2);
        this.tb303Panel2.render(document.getElementById('tb303_2Container'));

        this.tr808Panel = new TR808Panel(this.tr808, this.store, this.sequencer);
        this.tr808Panel.render(document.getElementById('tr808Container'));

        this.tr909Panel = new TR909Panel(this.tr909, this.store, this.sequencer);
        this.tr909Panel.render(document.getElementById('tr909Container'));

        this.minimoogPanel = new MinimoogPanel(this.minimoog, this.store, this.sequencer);
        this.minimoogPanel.render(document.getElementById('minimoogContainer'));

        this.ms20Panel = new KorgMS20Panel(this.ms20, this.store, this.sequencer);
        this.ms20Panel.render(document.getElementById('ms20Container'));

        this.jupiter8Panel = new Jupiter8Panel(this.jupiter8, this.store);
        this.jupiter8Panel.render(document.getElementById('jupiter8Container'));

        this.thereminPanel = new ThereminPanel(this.theremin, this.store);
        this.thereminPanel.render(document.getElementById('thereminContainer'));

        this.arp2600Panel = new ARP2600Panel(this.arp2600, this.sequencer, this.store);
        this.arp2600Panel.render('arp2600Container');

        this.juno106Panel = new Juno106Panel(this.juno106, this.store, this.sequencer);
        this.juno106Panel.render(document.getElementById('juno106Container'));

        this.sh101Panel = new SH101Panel(this.sh101, this.store, this.sequencer);
        this.sh101Panel.render(document.getElementById('sh101Container'));

        this.effectsPanel = new EffectsPanel(this.audioEngine, this.store);
        this.effectsPanel.render(document.getElementById('effectsContainer'));

        // Restaurer l'état du mixer
        if (this.mixerPanel) {
            this.mixerPanel.loadState(this.store.getMixerState());
        }
    }

    /**
     * Affiche brièvement l'indicateur de sauvegarde
     */
    showSaveIndicator() {
        const indicator = document.getElementById('saveIndicator');
        indicator.classList.add('visible');
        setTimeout(() => {
            indicator.classList.remove('visible');
        }, 2000);
    }
}
